/**
 * QualitasDisclaimerScroll — the insurer's wording, then Accept.
 *
 * This used to be a nested ScrollView inside the AML page scroller. On iOS
 * the parent wins the gesture, the inner box never reaches the bottom, and
 * Accept stays disabled. App Review will tap a dead checkbox and reject.
 *
 * Apple's own license sheets do not nest scrollers. The document sits in
 * the page. The checkbox sits after the last paragraph. Reaching it means
 * the text has been scrolled past. Checking it is the act of consent;
 * unchecking it is allowed. Submit still requires the box to be on.
 *
 * Qualitas requires the wording verbatim, including its capitalization.
 */
import { useEffect, useMemo } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/ui/Checkbox'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING, RADIUS, TEXT_STYLES } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import {
  QUALITAS_AML_DISCLAIMER_PARAGRAPHS_EN,
  QUALITAS_AML_DISCLAIMER_PARAGRAPHS_ES,
  QUALITAS_AML_PARTNER,
} from '@/lib/constants/aml'

const LINE_HEIGHT = TEXT_STYLES.bodySm.lineHeight
const BOX_PADDING = SPACING.md

interface QualitasDisclaimerScrollProps {
  accepted: boolean
  scrollCompleted: boolean
  onAcceptedChange: (accepted: boolean) => void
  onScrollCompleted: () => void
  error?: string
  testIDPrefix?: string
  /**
   * Recessed sits on a card already painted `colors.surface`, so the
   * document well uses `background` to read as a window into the text.
   */
  recessed?: boolean
}

export function QualitasDisclaimerScroll({
  accepted,
  scrollCompleted,
  onAcceptedChange,
  onScrollCompleted,
  error,
  testIDPrefix = 'aml.disclaimer',
  recessed = false,
}: QualitasDisclaimerScrollProps) {
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const paragraphs = useMemo(
    () =>
      i18n.language?.startsWith('en')
        ? QUALITAS_AML_DISCLAIMER_PARAGRAPHS_EN
        : QUALITAS_AML_DISCLAIMER_PARAGRAPHS_ES,
    [i18n.language],
  )

  // The page scroller is the only scroller. Once this document is in the
  // tree, the text has been presented; the remaining act is checking the
  // box that sits after the last paragraph.
  useEffect(() => {
    if (!scrollCompleted) onScrollCompleted()
  }, [scrollCompleted, onScrollCompleted])

  return (
    <View style={{ gap: SPACING.md }} testID={testIDPrefix}>
      <Text
        style={{
          fontSize: TEXT_STYLES.bodySm.fontSize,
          lineHeight: TEXT_STYLES.bodySm.lineHeight,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
        }}
      >
        {t('aml.disclaimer_helper')}
      </Text>

      <View
        style={{
          borderRadius: RADIUS.lg,
          backgroundColor: recessed ? colors.background : colors.surface,
          padding: BOX_PADDING,
          gap: SPACING.md,
        }}
        testID={`${testIDPrefix}.scroll`}
      >
        {paragraphs.map((paragraph, index) => (
          <Text
            key={`p-${index}`}
            style={{
              fontSize: TEXT_STYLES.bodySm.fontSize,
              lineHeight: LINE_HEIGHT,
              fontFamily: FONTS.body,
              color: colors.textPrimary,
              letterSpacing: 0.2,
            }}
          >
            {paragraph}
          </Text>
        ))}
      </View>

      <Checkbox
        checked={accepted}
        onChange={onAcceptedChange}
        error={error}
        testID={`${testIDPrefix}.accept`}
        label={
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: TEXT_STYLES.bodySm.fontSize,
              lineHeight: TEXT_STYLES.bodySm.lineHeight,
              color: colors.textPrimary,
              flex: 1,
            }}
          >
            {t('aml.disclaimer_accept_label', { partner: QUALITAS_AML_PARTNER.displayName })}
            <Text style={{ color: colors.textAccent }}> *</Text>
          </Text>
        }
      />
    </View>
  )
}
